"use client"

import Box from "@mui/material/Box"
import Stack from "@mui/material/Stack"
import Typography from "@mui/material/Typography"
import { alpha } from "@mui/material/styles"
import { ConfirmButton } from "@src/components/Button"
import Icons from "@src/components/Icons"
import Logo from "@src/components/logo"
import NextLink from "@src/components/NextLink"
import { ROUTES } from "@src/constants/routes"
import { usePathname } from "next/navigation"
import { useAppRouter } from "@src/hooks/use-app-router"
import * as React from "react"
import dynamic from "next/dynamic"
import BadgeIcon from "./badge-icon"
import MenuList from "./menu"
import LanguageSelector from "./language-selector"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import {
  selectTotalCartItems,
  selectTrendingKeywords
} from "@src/store/selectors/config"
import { useLayoutContext } from "./provider"

const DrawerSearch = dynamic(() => import("./search/search-drawer"), {
  ssr: false
})
const AccountPopover = dynamic(() => import("./account-popover"), {
  ssr: false
})

interface Props {
  onNavOpen: () => void
}

const TopNav = (props: Props) => {
  const { onNavOpen } = props
  const { t } = useTranslation()
  const router = useAppRouter()
  const pathname = usePathname()
  const { config, user } = useLayoutContext()
  const menuConfigList = config?.menuListConfig
  const trendingWords = useSelector(selectTrendingKeywords)
  const totalCartItems = useSelector(selectTotalCartItems)
  const cartBadgeNumber = totalCartItems

  const [openSearchDrawer, setOpenSearchDrawer] = React.useState(false)
  const [openAccountPopover, setOpenAccountPopover] = React.useState(false)
  const [searchMounted, setSearchMounted] = React.useState(false)
  const [accountMounted, setAccountMounted] = React.useState(false)
  React.useEffect(() => {
    if (openSearchDrawer) setSearchMounted(true)
    if (openAccountPopover) setAccountMounted(true)
  }, [openSearchDrawer, openAccountPopover])

  const accountAnchorEl = React.useRef(null)

  const ConfigMobilePageName = React.useMemo(
    () => [
      {
        path: ROUTES.RECRUITMENT,
        name: t("nav.recruitment")
      },
      {
        path: ROUTES.ABOUT_US,
        name: t("nav.about_us")
      },
      {
        path: ROUTES.MEMBERSHIP,
        name: t("nav.membership")
      },
      {
        path: ROUTES.BRANCH,
        name: t("nav.branch")
      },
      {
        path: ROUTES.FAQ,
        name: t("nav.faq")
      },
      {
        path: ROUTES.COLLECTION,
        name: t("nav.collections")
      },
      {
        path: ROUTES.PRODUCT,
        name: t("nav.products")
      },
      {
        path: ROUTES.PROFILE_ORDERS,
        name: t("nav.profile.order_management"),
        beforePath: "",
        isArrow: true
      },
      {
        path: ROUTES.PROFILE_NOTIFICATION,
        name: t("nav.profile.notifications"),
        beforePath: "",
        isArrow: true
      },
      {
        path: ROUTES.PROFILE_FAVORITE_PRODUCTS,
        name: t("nav.profile.favorite_products"),
        beforePath: "",
        isArrow: true
      },
      {
        path: ROUTES.PROFILE_INFO,
        name: t("nav.profile.personal_info"),
        beforePath: "",
        isArrow: true
      },
      {
        path: ROUTES.PROFILE_ADDRESS,
        name: t("nav.profile.address_book"),
        beforePath: "",
        isArrow: true
      },
      {
        path: ROUTES.PROFILE_ADDRESS_ADD,
        name: t("nav.profile.new_address"),
        beforePath: "",
        isArrow: true
      },
      {
        path: ROUTES.PROFILE_ADDRESS_EDIT,
        name: t("nav.profile.update_address"),
        isArrow: true
      },
      {
        path: ROUTES.PROFILE_VOUCHER,
        name: t("nav.profile.voucher"),
        isArrow: true
      },
      {
        path: ROUTES.PROFILE,
        name: t("nav.profile.profile_page"),
        beforePath: "",
        isArrow: true
      },
      {
        path: ROUTES.CART_PAYMENT,
        name: t("nav.cart.checkout"),
        beforePath: "",
        isArrow: true
      },
      {
        path: ROUTES.CART,
        name: t("nav.cart.shopping_cart"),
        beforePath: "",
        isArrow: true
      },
      {
        path: ROUTES.BLOG,
        name: t("nav.blog"),
        beforePath: "",
        isArrow: true
      }
    ],
    [t]
  )

  const currentRoute = React.useMemo(
    () => ConfigMobilePageName.find(item => pathname.startsWith(item.path)),
    [pathname]
  )

  const isPathnameProfile = React.useMemo(
    () => pathname.startsWith(ROUTES.PROFILE),
    [pathname]
  )

  const handleRedirect = path => () => {
    router.push(path)
  }

  const handleToggleSearchDrawer = (status: boolean) => () => {
    setOpenSearchDrawer(status ?? !openSearchDrawer)
  }

  const handleBackProfileMobile = () => {
    if (pathname === ROUTES.PROFILE) {
      router.push(ROUTES.HOME_PAGE)
    } else {
      router.push(ROUTES.PROFILE)
    }
  }

  return (
    <Box component="header" id="header">
      <Box
        sx={{
          backdropFilter: "blur(6px)",
          backgroundColor: theme => alpha(theme.palette.background.default, 1),
          position: "relative",
          zIndex: 1,
          margin: "auto",
          padding: {
            xs: "0 16px",
            md: "0 24px"
          },
          boxShadow: "4px 20px 20px -20px rgba(16, 24, 40, 0.06)"
        }}
      >
        <Stack
          justifyContent="space-between"
          alignItems="center"
          sx={{
            height: {
              xs: 56,
              lg: 76
            },
            maxWidth: "1440px",
            margin: "auto"
          }}
        >
          <Stack
            display={{
              xs: "flex",
              xl: "none"
            }}
            gap="8px"
            sx={{
              "& .MuiIconButton-root": {
                width: 32,
                height: 32,
                padding: "4px"
              },
              "& svg": {
                color: "#434343"
              }
            }}
          >
            {isPathnameProfile ? (
              <Icons
                name="arrow-left"
                type="button"
                onClick={handleBackProfileMobile}
              />
            ) : (
              <Icons name="hamburger" type="button" onClick={onNavOpen} />
            )}
          </Stack>

          {/* mobile site */}
          <Box
            display={{
              xs: "block",
              xl: "none"
            }}
            sx={{
              width: "100%",
              textAlign: "center"
            }}
            onClick={handleRedirect(currentRoute?.path || "/")}
          >
            {currentRoute?.name ? (
              <Typography
                variant="h3"
                sx={{
                  fontSize: "20px",
                  fontWeight: 700,
                  lineHeight: "25px",
                  paddingLeft: 0
                }}
              >
                {currentRoute?.name}
              </Typography>
            ) : (
              <Box
                sx={{
                  height: 16,
                  "& svg": {
                    width: "100%",
                    height: "100%"
                  }
                }}
                role="button"
                onClick={handleRedirect(ROUTES.HOME_PAGE)}
              >
                <Logo />
              </Box>
            )}
          </Box>

          {/* desktop site */}
          <Box
            sx={{
              cursor: "pointer",
              display: {
                xs: "none",
                xl: "flex"
              },
              alignItems: "center"
            }}
            role="button"
            // onClick={handleRedirect("/")}
          >
            <NextLink href={"/"}>
              <Logo />
            </NextLink>
          </Box>

          <Box
            width="100%"
            height="100%"
            sx={{
              display: {
                xs: "none",
                xl: "flex"
              },
              zIndex: 999
            }}
          >
            <MenuList
              menuConfigList={menuConfigList}
              currentPath={pathname}
              onRedirect={handleRedirect}
            />
          </Box>

          <Stack alignItems="center" spacing={1}>
            {/* Mobile */}
            <Stack
              display={{
                xs: "flex",
                xl: "none"
              }}
              gap="8px"
              sx={{
                "& .MuiIconButton-root": {
                  width: 32,
                  height: 32,
                  padding: "4px"
                },
                "& svg": {
                  color: "#434343"
                }
              }}
            >
              {isPathnameProfile ? (
                <>
                  <LanguageSelector />
                  <BadgeIcon
                    iconName="ecommerce"
                    badgeNumber={cartBadgeNumber}
                    onClick={handleRedirect(ROUTES.CART)}
                  />
                  <Icons
                    name="home"
                    type="button"
                    onClick={handleRedirect(ROUTES.HOME_PAGE)}
                  />
                </>
              ) : (
                <>
                  <Icons
                    name="search"
                    type="button"
                    onClick={() => setOpenSearchDrawer(true)}
                  />
                  <BadgeIcon
                    iconName="ecommerce"
                    badgeNumber={cartBadgeNumber}
                    onClick={handleRedirect(ROUTES.CART)}
                  />
                  <LanguageSelector />
                  <Icons
                    name="account"
                    type="button"
                    onClick={handleRedirect(
                      user ? ROUTES.PROFILE : ROUTES.SIGN_IN
                    )}
                  />
                </>
              )}
            </Stack>

            {/* Desktop */}
            <Stack
              display={{
                xs: "none",
                xl: "flex"
              }}
              gap="16px"
              alignItems="center"
              sx={{
                "& .MuiIconButton-root": {
                  width: 24,
                  height: 24,
                  padding: "0px"
                },
                "& svg": {
                  color: "#434343"
                }
              }}
            >
              <Icons
                name="search"
                type="button"
                sx={{
                  "& svg": {
                    color: "#434343"
                  }
                }}
                onClick={handleToggleSearchDrawer(true)}
              />
              <LanguageSelector />
              <BadgeIcon
                iconName="ecommerce"
                badgeNumber={cartBadgeNumber}
                onClick={handleRedirect(ROUTES.CART)}
              />
              <div ref={accountAnchorEl}>
                {user ? (
                  <ConfirmButton
                    sx={{
                      padding: "12px !important",
                      maxWidth: 150,
                      height: "36px !important",
                      minHeight: "36px !important",
                      "& span.label": {
                        maxWidth: 100,
                        overflow: "hidden",
                        display: "block",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap"
                      }
                    }}
                    label={<span className="label">{user?.full_name}</span>}
                    startIcon={
                      <Icons
                        sx={{
                          "& svg": {
                            color: "white !important"
                          }
                        }}
                        name="user"
                      />
                    }
                    onClick={() => setOpenAccountPopover(true)}
                  />
                ) : (
                  <ConfirmButton
                    sx={{
                      padding: "12px !important",
                      height: "36px !important",
                      minHeight: "36px !important"
                    }}
                    label={t("login")}
                    startIcon={
                      <Icons
                        sx={{
                          "& svg": {
                            color: "white !important"
                          }
                        }}
                        name="user"
                      />
                    }
                    onClick={() => {
                      setOpenAccountPopover(true)
                    }}
                  />
                )}
              </div>
            </Stack>
          </Stack>
        </Stack>
      </Box>
      {(openSearchDrawer || searchMounted) && (
        <DrawerSearch
          open={openSearchDrawer}
          toggleDrawer={handleToggleSearchDrawer}
          suggestKeyword={trendingWords}
        />
      )}
      {(openAccountPopover || accountMounted) && (
        <AccountPopover
          anchorEl={accountAnchorEl.current}
          open={!!openAccountPopover}
          onClose={() => setOpenAccountPopover(false)}
        />
      )}
    </Box>
  )
}

export default TopNav
